import type { BootstrapData, Category, Product, SessionInfo, StoreSettings, AdminUser } from './types';
import { demoData } from './demo';

let csrfToken = '';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (csrfToken && init.method && !['GET', 'HEAD'].includes(init.method)) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  const method = (init.method || 'GET').toUpperCase();
  const response = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
    cache: method === 'GET' || method === 'HEAD' ? 'no-store' : init.cache,
  });
  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = typeof data === 'string' ? data : data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : 'הפעולה נכשלה';
    throw new Error(message);
  }
  return data as T;
}

export const api = {
  bootstrap: () => import.meta.env.DEV && new URLSearchParams(location.search).has('demo')
    ? Promise.resolve(demoData)
    : request<BootstrapData>(`/public/bootstrap?fresh=${Date.now()}`, { cache: 'no-store' }),
  setupStatus: () => request<{ needsSetup: boolean }>('/setup/status'),
  setup: async (data: object) => {
    const result = await request<SessionInfo>('/setup', { method: 'POST', body: JSON.stringify(data) });
    csrfToken = result.csrfToken;
    return result;
  },
  login: async (username: string, password: string) => {
    const result = await request<SessionInfo>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
    csrfToken = result.csrfToken;
    return result;
  },
  recover: (recoveryToken: string, password: string) => request<{ ok: boolean }>('/auth/recover', { method: 'POST', body: JSON.stringify({ recovery_token: recoveryToken, password }) }),
  session: async () => {
    const result = await request<SessionInfo>('/admin/session');
    csrfToken = result.csrfToken;
    return result;
  },
  logout: async () => {
    await request('/auth/logout', { method: 'POST' });
    csrfToken = '';
  },
  categories: () => request<Category[]>('/admin/categories'),
  products: () => request<Product[]>('/admin/products'),
  createCategory: (data: object) => request<Category>('/admin/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: (id: string, data: object) => request<Category>(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCategory: (id: string) => request(`/admin/categories/${id}`, { method: 'DELETE' }),
  saveProduct: (id: string | null, form: FormData) => request<Product>(id ? `/admin/products/${id}` : '/admin/products', { method: id ? 'PUT' : 'POST', body: form }),
  setProductStatus: (id: string, status: string) => request<Product>(`/admin/products/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  deleteProduct: (id: string) => request(`/admin/products/${id}`, { method: 'DELETE' }),
  updateSettings: (settings: StoreSettings) => request<StoreSettings>('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }),
  users: () => request<AdminUser[]>('/admin/users'),
  createUser: (data: object) => request<AdminUser>('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id: string, data: object) => request<AdminUser>(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id: string) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  backupManifest: () => request<Record<string, unknown>>('/admin/backup'),
  restoreBackup: (file: File) => {
    const form = new FormData();
    form.append('backup', file);
    return request<BootstrapData>('/admin/restore', { method: 'POST', body: form });
  }
};
